import React, { useState, Fragment } from "react"
import { graphql, Link } from "gatsby"
import { Popover, Transition } from "@headlessui/react"
import { ChevronDownIcon } from "@heroicons/react/solid"

import Layout from "../components/common/layout"
import SEO from "../components/common/seo"
import TopicList from "../components/topic/topiclist"
import Categories from "../components/topic/categories"

import { convertToSlug, readableNumber } from "../utils/text"
import { classNames } from "../utils/tailwind"
import { sortTopics } from "../utils/topics"
import { useTopicListSort } from "../utils/communities"
import { getSchemaForCategory } from "../utils/schema"

const CategoryPage = ({ data, pageContext }) => {
  const isMobile = typeof window !== "undefined" && window.screen.width < 600
  const [topicFilter, setTopicFilter] = useState("")
  const { sort, setSort, sortOptions } = useTopicListSort()

  const topicsSource = data.categoryTopics.nodes
  const categoryRollup = {
    communities: topicsSource.reduce((s, t) => s + (Number(t.count) || 0), 0),
    pageviews: topicsSource.reduce((s, t) => {
      const vwc = t.viewsWithCommunities
      const pv =
        (vwc != null && vwc !== "" ? Number(vwc) : Number(t.views)) || 0
      return s + pv
    }, 0),
    redirects: topicsSource.reduce((s, t) => s + (Number(t.redirects) || 0), 0),
  }

  var categoryTopics = sortTopics(sort, topicsSource)

  const filterQ = topicFilter.trim().toLowerCase()
  const visibleTopics = filterQ
    ? categoryTopics.filter(
        t =>
          (t.name && t.name.toLowerCase().includes(filterQ)) ||
          (t.people &&
            String(t.people)
              .toLowerCase()
              .includes(filterQ)) ||
          (t.slug && t.slug.toLowerCase().includes(filterQ)) ||
          (t.category && t.category.toLowerCase().includes(filterQ))
      )
    : categoryTopics

  // assemble the categories from list of all topics
  var categories = []
  data.allTopics.nodes
    .filter(t => t.category)
    .forEach(t => {
      const index = categories.map(c => c.name).indexOf(t.category)
      if (index === -1) {
        categories.push({
          slug: convertToSlug(t.category),
          name: t.category,
          count: 1,
        })
      } else {
        categories[index].count += 1
      }
    })
  const allCategories = [
    { slug: "all", name: "All", count: data.allTopics.nodes.length },
    ...categories.sort((a, b) =>
      a.count < b.count ? 1 : b.count < a.count ? -1 : 0
    ),
    // {'slug': 'uncategorized', 'name': 'Uncategorized', 'count': data.allTopics.nodes.filter(t => !t.category).length},
  ]

  const categorySchemaDescription = `The best online communities for ${data.categoryTopics.nodes
    .map(t => t.name)
    .join(", ")}.`

  return (
    <Layout pageTitle={`${pageContext.name} Communities`} enableShare={true}>
      <SEO
        title={`${pageContext.name} Communities`}
        description={categorySchemaDescription}
        generateImage={true}
        path={`/category/${pageContext.slug}/`}
        schema={getSchemaForCategory({
          slug: pageContext.slug,
          name: pageContext.name,
          description: categorySchemaDescription,
          categoryTopicsNodes: data.categoryTopics.nodes,
        })}
      />

      <div className="grid grid-cols-3 sm:grid-cols-4 sm:gap-x-6 xl:gap-x-8">
        <div className="hidden sm:block">
          <Categories categories={allCategories} />
        </div>

        <div className="col-span-3 min-w-0">
          <div className="relative">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex flex-wrap items-center gap-y-2">
              <h2 className="pr-3 bg-white text-xl font-medium text-gray-900">
                {pageContext.name} community topics
              </h2>
              <span className="flex-shrink-0 font-semibold text-xs text-gray-500 bg-white px-1 pr-2">
                {filterQ
                  ? `${visibleTopics.length} / ${data.categoryTopics.nodes.length}`
                  : data.categoryTopics.nodes.length}
              </span>

              <div className="ml-auto flex flex-shrink-0 items-center gap-2 bg-white pl-2 sm:pl-4">
                <label htmlFor="topic-filter-category" className="sr-only">
                  Filter topics by name
                </label>
                <input
                  id="topic-filter-category"
                  type="search"
                  value={topicFilter}
                  onChange={e => setTopicFilter(e.target.value)}
                  placeholder="Search topics"
                  autoComplete="off"
                  className={classNames(
                    "mt-1 block w-32 sm:w-44 py-2 px-3 text-xs sm:text-sm text-gray-900 placeholder-gray-400",
                    "border border-gray-200 rounded-md bg-white",
                    "focus:outline-none focus:ring-1 focus:ring-rose-500 focus:border-rose-500"
                  )}
                />
                {sortOptions && sortOptions.length ? (
                  <Popover className="relative">
                    {({ open }) => (
                      <>
                        <Popover.Button
                          className={classNames(
                            open ? "text-gray-900" : "text-gray-500",
                            "group flex mt-1 min-w-[9rem] items-center justify-between gap-2 p-2 sm:px-3 sm:py-2 text-xs border-gray-300 focus:outline-none focus:ring-rose-500 border border-gray-200 focus:border-rose-500 sm:text-sm rounded-md"
                          )}
                        >
                          <span>{sort}</span>
                          <ChevronDownIcon
                            className={classNames(
                              open ? "text-gray-600" : "text-gray-400",
                              "hidden sm:block h-5 w-5 flex-shrink-0 group-hover:text-gray-500"
                            )}
                            aria-hidden="true"
                          />
                        </Popover.Button>

                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-200"
                          enterFrom="opacity-0 translate-y-1"
                          enterTo="opacity-100 translate-y-0"
                          leave="transition ease-in duration-150"
                          leaveFrom="opacity-100 translate-y-0"
                          leaveTo="opacity-0 translate-y-1"
                        >
                          <Popover.Panel className="absolute z-10 right-0 mt-3 px-2 w-36 sm:px-0">
                            <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
                              <div className="relative grid gap-1 bg-white p-2">
                                {sortOptions.map(t => (
                                  <div
                                    key={t}
                                    onClick={() => setSort(t)}
                                    className={`p-1 flex items-start cursor-pointer rounded-lg hover:bg-gray-50 transition ease-in-out duration-150 ${
                                      t === sort ? "bg-rose-50" : ""
                                    }`}
                                  >
                                    <div className="ml-0">
                                      <div className="text-base font-medium text-gray-900 flex">
                                        <div>{t}</div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </Popover.Panel>
                        </Transition>
                      </>
                    )}
                  </Popover>
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>

          <section
            className="mb-4 sm:mb-5 mt-2"
            aria-labelledby="category-rollup-heading"
          >
            <h3 id="category-rollup-heading" className="sr-only">
              {pageContext.name} category totals
            </h3>
            <dl className="grid grid-cols-1 gap-8 sm:grid-cols-3">
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Communities listed
                </dt>
                <dd className="mt-1 text-2xl font-semibold text-rose-600 tabular-nums">
                  {readableNumber(categoryRollup.communities)}
                </dd>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Monthly Pageviews
                </dt>
                <dd className="mt-1 text-2xl font-semibold text-rose-600 tabular-nums">
                  {readableNumber(categoryRollup.pageviews)}
                </dd>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Monthly Referrals
                </dt>
                <dd className="mt-1 text-2xl font-semibold text-rose-600 tabular-nums">
                  {readableNumber(categoryRollup.redirects)}
                </dd>
              </div>
            </dl>
          </section>
          {visibleTopics.length ? (
            <TopicList topics={visibleTopics} mini={true} cols={3} />
          ) : (
            <p className="mt-4 text-sm text-gray-500">
              No topics match that search.
            </p>
          )}
        </div>
      </div>
    </Layout>
  )
}

export const query = graphql`
  query CategoryPage($name: String) {
    categoryTopics: allGoogleSpreadsheetSourceTopics(
      sort: { fields: count, order: DESC }
      filter: { category: { eq: $name } }
    ) {
      totalCount
      nodes {
        slug
        isRoot
        name
        id
        count
        image
        people
        category
        visitors
        views
        viewsWithCommunities
        viewsWithCommunitiesPrev
        redirects
        added
        updated
      }
    }
    allTopics: allGoogleSpreadsheetSourceTopics(
      sort: { fields: count, order: DESC }
    ) {
      totalCount
      nodes {
        slug
        isRoot
        name
        id
        count
        image
        people
        category
        visitors
        views
        viewsWithCommunities
        viewsWithCommunitiesPrev
        added
        updated
      }
    }
  }
`

export default CategoryPage
